import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { deleteProtocol, getProtocols } from '../api/maintainenceInspectionApi';
import { Alert, AppBar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Toolbar, Typography } from "@mui/material"
import { useLocation, useParams, useNavigate } from "react-router-dom"
import ProtocolActionRenderer from '../components/ProtocolActionRenderer'
import AddReport from "../components/AddReport";
import CloseIcon from "@mui/icons-material/Close";
import PdfViewerDialog from "../components/PDFViewerDialog";
import { getIntlTranslation } from "../../../utils/helper";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { getStatus } from "../utils/statusUtils";
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { miRoot } from "../../../utils/defaultValues";

const MIProtocolView = () => {

    const intl = useIntl()
    const language = useSelector((state) => state.system.language);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const inspectionId = location.state?.element?.id;

    const [rowData, setRowData] = useState([]);
    const { siteId } = useParams();
    const [addProtocol, setAddProtocol] = useState(false)
    const [openDelete, setOpenDelete] = useState(false)
    const [viewPdf, setViewPdf] = useState(false)
    const [pdfUrl, setPdfUrl] = useState(null)
    const [isLatestProtocolUploaded, setIsLatestProtocolUploaded] = useState(null)
    const [rowToDelete, setRowToDelete] = useState(null)
    const [title, setTitle] = useState('')
    const [status, setStatus] = useState('')

    useEffect(()=>{
        console.log(location.state, 'location state in protocol view')
    })

    const onDelete = (row) => {
        console.log('on delete')
        setOpenDelete(true)
        setRowToDelete(row)
    }

    const confirmDelete = async () => {
        setOpenDelete(false)
        await dispatch(deleteProtocol(rowToDelete.id));
        await fetchProtocols();
    }

    const onView = (row) => {
        setOpenDelete(false)
        setViewPdf(true)
        setPdfUrl(row.fileUrl)
        setTitle(row.title)
        console.log(row.fileUrl)
    }

    const closePdf = () => {
        setViewPdf(false);
        setPdfUrl(null);
    };

    const onDownload = async (url, filename) => {
        const response = await fetch(url);
        const blob = await response.blob();

        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(blobUrl);
    };

    const fetchProtocols = useCallback(async () => {
        if (!inspectionId) return;
        const data = await dispatch(getProtocols(inspectionId));

        if (data && Array.isArray(data)) {
            setRowData(
                data.map((item, idx) => ({
                    id: item.id || idx + 1,
                    title: item.title,
                    protocolDate: item.protocolDate,
                    version: item.version,
                    fileUrl: item.fileUrl || "",
                }))
            );
        } else {
            setRowData([]);
        }

        // update local flag
        setIsLatestProtocolUploaded(location.state.element.protocolsUploaded);
    }, [inspectionId, dispatch, location.state.element.protocolsUploaded]);
    // 🔹 Call once when the component loads

    useEffect(() => {
        fetchProtocols();
    }, [fetchProtocols]);

    useEffect(() => {
        const currentStatus = getStatus({ nextStr: location.state.element.nextInspectionDate, reinspectionStr:location.state.element.reinspectionRequired});
        setStatus(currentStatus);
    }, [location.state.element.nextInspectionDate, location.state.element.reinspectionRequired]);

    const columns = useMemo(
        () => [
            {
                field: "id",
                headerName: intl.formatMessage({ id: "mi.id" }),
                minWidth: 80,
                flex: 0.5,
            },
            {
                field: "title",
                headerName: intl.formatMessage({ id: "user.title" }),
                minWidth: 220,
                flex: 1.2,
            },
            {
                field: "protocolDate",
                headerName: intl.formatMessage({ id: "mi.protocol-date" }),
                minWidth: 160,
                flex: 0.8,
                valueGetter: (params) => {
                    const date = new Date(params.row.protocolDate);
                    return isNaN(date.getTime()) ? "-" : date.toLocaleDateString("de-DE");
                },
            },
            {
                field: "version",
                headerName: intl.formatMessage({ id: "mi.version" }),
                minWidth: 120,
                flex: 0.6,
            },
            {
                field: "actions",
                headerName: intl.formatMessage({ id: "mi.actions" }),
                minWidth: 150,
                flex: 0.7,
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                    <ProtocolActionRenderer
                        onDelete={() => onDelete(params.row)}
                        onView={() => onView(params.row)}
                        onDownload={() =>
                            onDownload(params.row.fileUrl, `${params.row.title}.pdf`)
                        }
                    />
                ),
            },
        ],
        [intl, language]
    );

    return (
        <>
            <Box sx={{ margin: 1, }}>
                <Button
                    variant="none"
                    startIcon={<ArrowBackIcon />}
                    onClick={() =>  navigate(`${miRoot}/site-overview/${siteId}`, { state: { site:location.state.site } })}
                    sx={{ mb: 2, color: '#254a9a', borderColor: '#254a9a' }}
                >
                    {intl.formatMessage({ id: '_back' })}
                </Button>
                <Typography
                    variant="h5"
                    noWrap
                    component="div"
                    sx={{
                        flexGrow: 1,
                        display: { xs: "none", sm: "block", width: "40%" },
                        fontWeight: 600, color: '#254a9a'
                    }}
                >
                    {location.state.element.elementName}
                </Typography>
            </Box>
            {!isLatestProtocolUploaded ?
                <Alert
                    severity={status === 'overdue' ? "error" : status === 'upcoming' ? "warning" : 'success'}
                    action={
                        status !== 'completed' &&
                        <Button onClick={() => setAddProtocol(true)} color="inherit" size="small" variant="outlined" sx={{ border: '1px solid', borderColor: status === 'overdue' ? '#aa3939' : status === 'upcoming' ? '#ffc924' : '#97b444', borderRadius: 1, boxShadow: 2 }}>
                            {intl.formatMessage({ id: "mi.upload-new-protocol" })}
                        </Button>
                    }
                    sx={{ margin: 1 }}
                >
                    {intl.formatMessage({ id: `mi.${status}` })}: {status === 'completed' ? intl.formatMessage({ id: 'mi.all-set' }) : intl.formatMessage({ id: 'mi.new-protocol' })}
                </Alert> : null}
            <Box sx={{ width: '100%', overflowX: 'auto', mt: 1, height: 'calc(100vh - 220px)', mb:1}}>
                <DataGrid
                    rows={rowData}
                    columns={columns}
                    disableRowSelectionOnClick
                    pageSizeOptions={[5, 10, 25]}
                    initialState={{
                        pagination: {
                            paginationModel: { page: 0, pageSize: 10 },
                        },
                    }}
                    sx={{
                            bgcolor: '#fff',
                            "& .MuiDataGrid-columnHeaderTitle": {
                                fontWeight: 700,
                            },
                            "& .MuiDataGrid-cell": {
                                display: "flex",
                                alignItems: "center",
                                px: 2
                            },
                            "& .MuiDataGrid-cell--textCenter": {
                                justifyContent: "center",
                            },
                            "& .MuiDataGrid-columnHeader--alignCenter": {
                                justifyContent: "center",
                            },
                            "& .MuiDataGrid-columnHeader": {
                                px: 2,
                            },
                        }}
                    density="standard"
                />
            </Box>

            {/* <Box sx={{ position: 'fixed', bottom: 40, right: 30, zIndex: 140 }}>
                <SaveButton
                    id="add-report-button"
                    buttonText="Upload New Protocol"
                    variant="button"
                    size="medium"
                    style={{
                        padding: "8px 16px",
                        fontSize: "0.9rem",
                        minWidth: "auto",
                        lineHeight: 1.25,
                    }}
                    tooltip={{ title: '' }}
                    handleClick={(e) => {
                        console.log(addProtocol, 'openAdd Report');
                        setAddProtocol(true)
                    }}
                />
            </Box> */}

            <PdfViewerDialog
                open={viewPdf}
                url={pdfUrl}
                title={title}
                onClose={closePdf}
            />
            {openDelete ?
                <Dialog
                    open={openDelete}
                    onClose={() => setOpenDelete(false)}
                    fullWidth
                    maxWidth="md"
                    TransitionProps={{ unmountOnExit: true }}
                    PaperProps={{ sx: { borderRadius: 2 } }}
                >
                    <AppBar
                        className="appBar"
                        position="relative"
                        sx={{ bgcolor: "background.paper", color: "text.primary", }}
                    >
                        <Toolbar className="dialog-toolbar" sx={{ display: "flex", justifyContent: "space-between", }}>
                            <DialogTitle>{intl.formatMessage({ id: "mi.delete-protocol" })}</DialogTitle>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <IconButton
                                    edge="end"
                                    color="inherit"
                                    aria-label="close"
                                    onClick={() => setOpenDelete(false)}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Toolbar>
                    </AppBar >
                    <DialogContent>
                        {getIntlTranslation("mi.delete-confirmation")} "{rowToDelete.title}"?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDelete(false)}>{intl.formatMessage({ id: "cancel" })}</Button>
                        <Button onClick={confirmDelete} color="error" variant="contained">{intl.formatMessage({ id: "delete" })}</Button>
                    </DialogActions>
                </Dialog> : null}
            {addProtocol ? <AddReport
                open={addProtocol}
                onClose={() => {
                    setAddProtocol(false)
                    fetchProtocols();
                }}
                onUploaded={async () => {
                    await fetchProtocols();
                }}
                protocol={true}
                inspectionId={location.state.element.id}
            /> : null}
        </>
    )
}

export default MIProtocolView